import { DEFAULT_LANGUAGE_RECORD } from "components/LanguageSelector";
import Translation from "components/Translation";
import { tenant_logo_url, useAuth } from "context/AuthContext";
import { useTenantContext } from "context/TenantContext";
import Slider from "react-slick";
import { LocationItem } from "types/location";
import { classNames } from "utils/dom-helpers";
import { ENTRY_POINTS, IEntrypointOption } from "./EntryPointSelector";
import LocationCardPreview from "./LocationCardPreview";
import ServiceCardPreview from "./ServiceCardPreview";
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import MembershipCardPreview from "./MembershipCardPreview";
import { MembershipApiItem } from "types/membership";
import { useEffect, useState } from "react";
import { CategoryApiOutput } from "types/category";
import CategoryCardPreview from "./CategoryCardPreview";
import { ServiceItem } from "types/service";
import getConfig from "next/config";

type PreviewBookingPageProps = {
    show_intro_message: boolean
    intro_message: string
    show_intro_border: boolean
    entry_point: IEntrypointOption
    allow_entry_by_category: boolean
    show_services_include: boolean
    primary_color: string
    image_url?: string
}

export default function PreviewBookingPage(props: PreviewBookingPageProps) {
    const {
        show_intro_message,
        intro_message,
        show_intro_border,
        entry_point,
        allow_entry_by_category,
        show_services_include,
        primary_color,
        image_url
    } = props
    const {
        locations,
        services: service_cache,
        all_memberships,
        tenant,
        service_categories,
        membership_categories
    } = useTenantContext()
    const [memberships, setMemberships] = useState<MembershipApiItem[]>([])
    const [services, setServices] = useState<ServiceItem[]>(service_cache)
    const [categories, setCategories] = useState<CategoryApiOutput[]>([])
    const [selected_category, setSelectedCategory] = useState<CategoryApiOutput | undefined>(undefined)
    const [is_mobile_view, toggleMobileView] = useState<boolean>(false)
    const { NEXT_PUBLIC_STATIC_DOMAIN } = getConfig().publicRuntimeConfig

    function locationSelector() {
        return (
            <>
                <Translation
                    render_as="h1"
                    content_key="booking_page.select_location"
                    className="flex mx-auto text-[3px] w-fit"
                />
                <button className="flex border-gray-200 rounded-[2px] border-[1px] px-[3px] py-[2px] w-fit mx-auto">
                    <span className="flex items-center text-[3px]">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-1 w-1 mr-[2px]"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                            />
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                            />
                        </svg>
                        {locations?.[0]?.name}
                    </span>
                </button>
            </>
        )
    }

    function aotLogo() {
        return (
            <div className="mt-[1px] text-[3px]">
                <img
                    className="mx-auto h-[10px] cursor-pointer hover:shadow-blue-300/50 hover:shadow-lg"
                    src={`//${NEXT_PUBLIC_STATIC_DOMAIN}/images/powered-by-lg.png`}
                    alt="Open to New Page"
                />
            </div>
        )
    }

    function renderSelector() {
        if (allow_entry_by_category && !selected_category && categories?.length > 0) {
            return (
                <div className='flex flex-wrap gap-[5px] max-h-loose mb-[3px] justify-evenly px-[5px] mt-[10px]'>
                    {categories?.map((item) => {
                        const service_list = services.filter(
                            (i) => i.category?.id === item.id
                        )
                        const fallback_img = service_list.find(
                            (item) => item.image_url
                        )?.image_url

                        return (
                            <CategoryCardPreview
                                key={item.id}
                                category={item}
                                fallback_img={fallback_img}
                                entry_list={service_list}
                                primary_color={primary_color}
                                setSelectedCategory={setSelectedCategory}
                                show_services_include={show_services_include}
                            />
                        )
                    })}
                </div>
            )
        } else {
            switch (entry_point) {
                case ENTRY_POINTS[0]:
                    return (
                        <>
                            <Translation
                                render_as="p"
                                content_key="booking_page.select_location"
                                className="font-semibold text-[4px] text-center"
                                style={{
                                    color: primary_color as string,
                                }}
                            />
                            <div className="flex flex-row w-full flex-wrap justify-center py-0 gap-1.5 mt-1.5">
                                {locations?.map((loc: LocationItem, idx: number) =>
                                    <LocationCardPreview
                                        primary_color={primary_color}
                                        location={loc}
                                        key={loc.id}
                                    />
                                )}
                            </div>
                        </>
                    )
                    break
                case ENTRY_POINTS[1]:
                    return (
                        <>
                            <div
                                className='flex flex-wrap gap-1.5 w-3/4 mx-auto max-h-min h-fit mb-1 justify-evenly'
                                id="service-preview"
                            >
                                <h1
                                    className=" text-center font-bold text-[4px]"
                                    style={{
                                        color: primary_color
                                    }}
                                >
                                    <Translation content_key='booking.select_a_service' />
                                </h1>
                                <div className="w-full align-middle h-full">
                                    <Slider
                                        {...{
                                            className: 'center mx-6',
                                            dots: services?.length > 5,
                                            infinite: true,
                                            speed: 500,
                                            slidesToShow: services?.length > 1 ? 2 : 1,
                                            slidesToScroll: 1,
                                            pauseOnHover: true,
                                            swipeToSlide: true,
                                            arrows: services?.length > 5,
                                            swipe: services?.length > 5,
                                            rows:
                                                services?.length > 6
                                                    ? 3
                                                    : services?.length > 2
                                                        ? 2
                                                        : 1,
                                            responsive: [
                                                {
                                                    breakpoint: 600,
                                                    settings: {
                                                        adaptiveHeight: services?.length > 5,
                                                        initialSlide: 1,
                                                        rows: 5,
                                                        slidesToScroll: 1,
                                                        slidesToShow: 1,
                                                    },
                                                }
                                            ],
                                        }}
                                    >
                                        {services?.map((service) => {
                                            return (
                                                <ServiceCardPreview
                                                    primary_color={primary_color}
                                                    key={service?.id}
                                                    service={service}
                                                />
                                            )
                                        })}
                                    </Slider>
                                </div>
                                {aotLogo()}
                            </div >
                        </>
                    )
                    break
                case ENTRY_POINTS[2]:
                    return (
                        <div className="w-3/4 mx-auto">
                            <div className='flex flex-wrap gap-[5px] w-full mt-[5px] mb-[3px] justify-evenly'>
                                {memberships.map((item) => (
                                    <MembershipCardPreview
                                        key={item.id}
                                        membership={item}
                                        primary_color={primary_color}
                                    />
                                ))}
                            </div>
                        </div>
                    )
                    break
            }
        }
    }

    useEffect(() => {
        if (entry_point == ENTRY_POINTS[1]) {
            let filtered_services = service_cache.filter((service) => !service.is_archived)
            if (allow_entry_by_category) {
                setCategories(service_categories.filter((category) => !category.deletedAt && filtered_services.some((service) => service.category.id == category.id)))
            } else {
                setServices(filtered_services)
            }
        } else if (entry_point == ENTRY_POINTS[2]) {
            let filtered_memberships = all_memberships.filter((membership) => !membership.deletedAt)
            if (allow_entry_by_category) {
                setCategories(membership_categories.filter((category) => !category.deletedAt && filtered_memberships.some((membership) => membership.categoryId == category.id)))
            } else {
                setMemberships(filtered_memberships)
            }

        }
    }, [all_memberships, service_cache, entry_point, allow_entry_by_category, service_categories, membership_categories])

    useEffect(() => {
        if (selected_category) {
            if (entry_point == ENTRY_POINTS[1]) {
                setServices(service_cache.filter((service) => service.category.id == selected_category.id))
            } else if (entry_point == ENTRY_POINTS[2]) {
                setMemberships(all_memberships.filter((membership) => membership.categoryId == selected_category.id))
            }
        }
    }, [selected_category, service_cache, all_memberships])

    return (
        <>
            <div className="flex flex-row justify-between">
                <Translation
                    content_key='common.preview'
                    render_as='h3'
                    className='flex w-fit h-fit bg-gray-four rounded-[20px] py-1 px-4 text-neutral-700 font-[450]'
                />
                <div hidden className='flex w-fit h-fit bg-transparent rounded-[10px] text-neutral-700 font-[450] text-lg gap-0.5 opacity-90'>
                    <button
                        className={classNames("text-center items-center rounded-l-[10px] pt-1.5 pb-1 px-4", is_mobile_view ? "bg-primary-light text-white" : " bg-gray-four")}
                        onClick={() => {
                            toggleMobileView(true)
                        }}
                        type="button"
                    >
                        <i className="feather-smartphone" />
                    </button>
                    <button
                        className={classNames("text-center items-center rounded-r-[10px] pt-1.5 pb-1 px-4", !is_mobile_view ? "bg-primary-light text-white" : "bg-gray-four")}
                        onClick={() => {
                            toggleMobileView(false)
                        }}
                        type="button"
                    >
                        <i className="feather-layout" />
                    </button>
                </div>
            </div>
            <div className='flex flex-col my-auto gap-y-10 px-10'>
                <div className='flex flex-col mb-10 h-[280px] w-full bg-white'>
                    <div className="flex flex-row w-full justify-between bg-white py-1 px-0.5">
                        <img
                            src={tenant?.bizLogoUrl || tenant_logo_url}
                            className="w-4 h-4"
                            alt="tenant logo"
                        />
                        <div className="flex flex-row gap-x-1">
                            <div
                                className="border w-9 h-[13px] py-[1.5px] rounded-[3px] text-center text-[5px]"
                                style={{
                                    borderColor: primary_color,
                                    color: primary_color
                                }}
                            >
                                <Translation content_key="booking.sign_up" />
                            </div>
                            <div
                                className="rounded-[3px] w-9 h-[13px] py-[2.5px] text-center text-white text-[5px]"
                                style={{
                                    backgroundColor: primary_color
                                }}
                            >
                                <Translation content_key="booking.login" />
                            </div>
                            <div className="flex h-[13px] px-[2.73px] py-[1.64px] border rounded-md border-gray-150 text-center items-center text-[4px] gap-x-[1px]">
                                <img
                                    src={DEFAULT_LANGUAGE_RECORD.image_url}
                                    className="rounded-full w-2 h-2"
                                    alt="Selected Language Image"
                                />
                                EN
                                <i
                                    className="feather-chevron-down text-primary text-[7px]"
                                    aria-hidden="true"
                                />
                            </div>
                        </div>
                    </div>
                    <div className="relative w-full h-full">
                        <img
                            src={image_url || `https://${NEXT_PUBLIC_STATIC_DOMAIN}/images/booking_background.jpg`}
                            className="w-full absolute h-full overflow-hidden"
                            alt="background"
                        />
                        <div
                            className="flex-col absolute inset-0 m-auto w-[80%] h-fit max-h-[183px] bg-white z-10 items-center overflow-y-auto overflow-x-hidden py-[13px] shadow-dialog rounded-[4px]"
                            style={{
                                scrollbarWidth: "thin"
                            }}
                        >
                            <button
                                hidden={!selected_category}
                                onClick={() => setSelectedCategory(undefined)}
                                className="absolute top-[3px] left-[3px] cursor-pointer px-[7px] py-[2px] border-primary border-[1px] rounded-[3px] text-primary hover:bg-primary hover:text-white"
                                type="button"
                            >
                                <span className="flex items-center text-[4px]">
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-[4px] w-[4px] mr-[2px]"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            d="M7 16l-4-4m0 0l4-4m-4 4h18"
                                        />
                                    </svg>
                                    <Translation content_key="back" />
                                </span>
                            </button>
                            <img
                                src={tenant?.bizLogoUrl || tenant_logo_url}
                                className="h-[30px] w-[30px] mx-auto"
                                alt="tenant logo"
                            />
                            <Translation
                                content_key="booking_page.welcome_to_tenant"
                                content_values={{
                                    tenant_name: tenant?.name as unknown as string,
                                }}
                                render_as="div"
                                className="text-[8px] text-black text-center font-bold mt-1"
                            />
                            {show_intro_message
                                && intro_message != undefined
                                && intro_message.length > 0
                                && intro_message
                                    ?.replace(/<\/?[^>]+(>|$)/g, '')
                                    ?.replace(/ /g, '')?.length && (
                                    <div
                                        className={classNames(
                                            'w-[100px] rounded-[4px] py-1 px-[2px] mb-[1px] mx-auto',
                                            show_intro_border
                                                ? 'border-gray-200 border-[1px] shadow-md backdrop-blur-sm'
                                                : ''
                                        )}
                                        style={{
                                            transform: "scale(0.4)"
                                        }}
                                        dangerouslySetInnerHTML={{
                                            __html: intro_message,
                                        }}
                                    />
                                )}
                            {entry_point != ENTRY_POINTS[0] &&
                                <div className="w-full flex-col mx-auto">
                                    {locationSelector()}
                                </div>
                            }
                            {renderSelector()}
                        </div>
                    </div>
                </div>

                <div hidden className='flex h-[280px] w-full bg-white'>
                    {/* TODO: render class, appt and membership pages here */}
                </div>
            </div>
        </>
    )
}